/**
 * components/JournalReviewDrawer.tsx — MER Algo Footprints v7.1H
 * Hedge Fund Mode — Section 5
 *
 * Collapsed by default during live trading.
 * Expands to show review state, minimal user inputs, and draft actions.
 * Lightweight — must not dominate the live view.
 */

import React, { useState } from 'react';
import { HedgeFundModeViewModel } from '../types/hedgeFundViewModel';

interface Props {
  vm:            HedgeFundModeViewModel;
  onAccept?:     () => void;
  onDiscard?:    () => void;
  forceOpen?:    boolean;   // open automatically when draft exists
}

type TradeDecision = 'took_it' | 'passed' | null;
type FollowedPlan  = 'yes' | 'no' | 'partial' | null;

const PASS_REASONS = [
  'Confirmation missing',
  'Low RVOL',
  'Countertrend risk',
  'Too extended',
  'Trap risk',
  'Personal risk limit',
];

export function JournalReviewDrawer({ vm, onAccept, onDiscard, forceOpen = false }: Props) {
  const { review } = vm;
  const hasActivity = review.journalDraftAvailable || review.aiReviewAvailable;

  const [open,         setOpen]         = useState(forceOpen || hasActivity);
  const [decision,     setDecision]     = useState<TradeDecision>(null);
  const [passReason,   setPassReason]   = useState<string | null>(null);
  const [followedPlan, setFollowedPlan] = useState<FollowedPlan>(null);
  const [note,         setNote]         = useState('');

  const canAccept = decision !== null && (decision !== 'passed' || passReason !== null);

  const handleAccept = () => {
    if (onAccept) onAccept();
  };

  // Collapsed state — single row trigger
  if (!open) {
    return (
      <div
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px',
          background: 'var(--surface1)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--blue-muted)', fontFamily: 'var(--mono)', letterSpacing: 1.2 }}>
            JOURNAL / REVIEW
          </span>
          {review.journalDraftAvailable && (
            <span style={{ fontSize: 9, color: 'var(--gold)', fontFamily: 'var(--mono)', background: 'rgba(201,168,76,0.1)', padding: '1px 6px', borderRadius: 3, border: '1px solid rgba(201,168,76,0.3)' }}>
              DRAFT PENDING
            </span>
          )}
          {review.snapshotId && (
            <span style={{ fontSize: 9, color: 'var(--purple)', fontFamily: 'var(--mono)' }}>
              SNAP · {review.snapshotId.slice(0, 8)}
            </span>
          )}
        </div>
        <span style={{ fontSize: 12, color: 'var(--blue-dim)' }}>▼</span>
      </div>
    );
  }

  // Expanded state
  return (
    <div style={{
      background: 'var(--surface1)',
      border: '1px solid var(--border)',
      borderRadius: 6,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div
        onClick={() => setOpen(false)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px',
          borderBottom: '1px solid var(--border)',
          cursor: 'pointer', userSelect: 'none',
          background: 'var(--surface2)',
        }}
      >
        <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--blue-muted)', fontFamily: 'var(--mono)', letterSpacing: 1.2 }}>
          JOURNAL / REVIEW
        </span>
        <span style={{ fontSize: 12, color: 'var(--blue-dim)' }}>▲</span>
      </div>

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Snapshot status */}
        {(review.snapshotId || review.outcomeStatus) && (
          <div style={{ display: 'flex', gap: 16 }}>
            {review.snapshotId && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', letterSpacing: 1 }}>SNAPSHOT</span>
                <span style={{ fontSize: 11, color: 'var(--text)', fontFamily: 'var(--mono)' }}>{review.snapshotId.slice(0, 12)}…</span>
              </div>
            )}
            {review.outcomeStatus && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', letterSpacing: 1 }}>OUTCOME</span>
                <span style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'var(--mono)' }}>{review.outcomeStatus}</span>
              </div>
            )}
          </div>
        )}

        {/* Q1 — Did you take it? */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 9, color: 'var(--blue-muted)', fontFamily: 'var(--mono)', letterSpacing: 1 }}>
            DID YOU TAKE IT?
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['took_it', 'passed'] as TradeDecision[]).map(d => (
              <button
                key={d!}
                onClick={() => setDecision(d)}
                style={{
                  padding: '5px 16px', borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)',
                  fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${decision === d ? (d === 'took_it' ? '#4ade80' : '#f87171') : 'var(--border)'}`,
                  background: decision === d
                    ? (d === 'took_it' ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)')
                    : 'transparent',
                  color: decision === d
                    ? (d === 'took_it' ? '#4ade80' : '#f87171')
                    : 'var(--blue-muted)',
                }}
              >
                {d === 'took_it' ? 'Took it' : 'Passed'}
              </button>
            ))}
          </div>
        </div>

        {/* Q2 — If passed, why? */}
        {decision === 'passed' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 9, color: 'var(--blue-muted)', fontFamily: 'var(--mono)', letterSpacing: 1 }}>
              WHY DID YOU PASS?
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {PASS_REASONS.map(r => (
                <button
                  key={r}
                  onClick={() => setPassReason(passReason === r ? null : r)}
                  style={{
                    padding: '3px 10px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--mono)',
                    cursor: 'pointer',
                    border: `1px solid ${passReason === r ? 'var(--gold)' : 'var(--border)'}`,
                    background: passReason === r ? 'rgba(201,168,76,0.1)' : 'transparent',
                    color: passReason === r ? 'var(--gold)' : 'var(--blue-dim)',
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Q3 — Did you follow plan? (only if took it) */}
        {decision === 'took_it' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 9, color: 'var(--blue-muted)', fontFamily: 'var(--mono)', letterSpacing: 1 }}>
              DID YOU FOLLOW THE PLAN?
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['yes', 'partial', 'no'] as FollowedPlan[]).map(f => (
                <button
                  key={f!}
                  onClick={() => setFollowedPlan(f)}
                  style={{
                    padding: '4px 12px', borderRadius: 4, fontSize: 10, fontFamily: 'var(--mono)',
                    cursor: 'pointer',
                    border: `1px solid ${followedPlan === f ? 'var(--blue)' : 'var(--border)'}`,
                    background: followedPlan === f ? 'rgba(59,130,246,0.1)' : 'transparent',
                    color: followedPlan === f ? 'var(--blue)' : 'var(--blue-dim)',
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Optional note */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', letterSpacing: 1 }}>
            NOTE (OPTIONAL)
          </span>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Any additional context…"
            rows={2}
            style={{
              background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 4, color: 'var(--text)', fontFamily: 'var(--mono)',
              fontSize: 11, padding: '6px 8px', resize: 'vertical', outline: 'none',
            }}
          />
        </div>

        {/* Draft actions */}
        <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
          <button
            onClick={handleAccept}
            disabled={!canAccept}
            style={{
              flex: 1, padding: '7px 0', borderRadius: 4,
              background: canAccept ? 'rgba(74,222,128,0.1)' : 'transparent',
              border: `1px solid ${canAccept ? 'rgba(74,222,128,0.4)' : 'var(--border)'}`,
              color: canAccept ? '#4ade80' : 'var(--blue-dim)',
              fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)',
              cursor: canAccept ? 'pointer' : 'default',
            }}
          >
            ACCEPT
          </button>
          <button
            onClick={onDiscard}
            style={{
              flex: 1, padding: '7px 0', borderRadius: 4,
              background: 'transparent', border: '1px solid var(--border)',
              color: 'var(--blue-muted)',
              fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer',
            }}
          >
            DISCARD
          </button>
        </div>

      </div>
    </div>
  );
}
